import React, { createContext, useContext, useState } from 'react';
import type { States, Methods, Context } from './types';

interface CalculateReturnOfInvestmentsProviderProps {
	children: JSX.Element;
}

const KEYBOARD_ENTER = 'Enter';

const CalculateReturnOfInvestmentsContext = createContext<Context>({} as Context);

export function CalculateReturnOfInvestmentsProvider({
	children
}: Readonly<CalculateReturnOfInvestmentsProviderProps>) {
	const [roiId, setRoiId] = useState<string>('');
	const [currentStep, setCurrentStep] = useState<number>(0);

	function handleOnUpdateId(id: string): void {
		setRoiId(id);
	}

	function handleOnNextStep(): void {
		setCurrentStep((prevState) => prevState + 1);
	}

	function handleOnPreviousStep(): void {
		setCurrentStep((prevState) => prevState - 1);
	}

	function handleOnUpdateStep(step: number): void {
		setCurrentStep(step);
	}

	function handleOnKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
		if (e.key === KEYBOARD_ENTER) e.preventDefault();
	}

	const states: States = {
		id: roiId,
		currentStep
	};

	const methods: Methods = {
		onKeyDown: handleOnKeyDown,
		onUpdateId: handleOnUpdateId,
		onNextStep: handleOnNextStep,
		onPreviousStep: handleOnPreviousStep,
		onUpdateCurrentStep: handleOnUpdateStep
	};

	const context: Context = {
		...states,
		...methods
	};

	return (
		<CalculateReturnOfInvestmentsContext.Provider value={context}>
			{children}
		</CalculateReturnOfInvestmentsContext.Provider>
	);
}

export function useCalculateReturnOfInvestments(): Context {
	const context = useContext(CalculateReturnOfInvestmentsContext);
	if (!context) {
		throw new Error('useCalculateReturnOfInvestments must be used within a CalculateReturnOfInvestmentsContext');
	}
	return context;
}
